import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

/**
 * A labelled control with its hint and its error wired up.
 *
 * The wiring is the point. A hint that is not in `aria-describedby` is invisible to
 * a screen reader, and an error announced only in colour is invisible to anyone who
 * cannot see it — so both are associated here rather than at each call site, where
 * one of them would eventually be forgotten.
 */

interface CommonProps {
  label: string;
  /**
   * `| undefined` explicitly, because `exactOptionalPropertyTypes` otherwise
   * refuses a caller that computes a hint and may not have one — which is every
   * caller that builds one from data rather than writing it inline.
   */
  hint?: string | undefined;
  error?: string | null;
  /**
   * Keeps the label for a screen reader and takes it off the screen.
   *
   * For a control that already sits under a visible heading of its own — a
   * settings row whose checkbox carries the name — where showing it twice reads as
   * two separate things.
   */
  labelHidden?: boolean;
}

export type TextFieldProps = CommonProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'aria-describedby' | 'aria-invalid'>;

export function TextField({
  label,
  hint,
  error,
  labelHidden = false,
  ...input
}: TextFieldProps): ReactNode {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint === undefined ? '' : hintId, error ? errorId : ''].filter(Boolean);

  return (
    <div className="field">
      <label htmlFor={id} className={labelHidden ? 'visually-hidden' : undefined}>
        {label}
      </label>
      {hint !== undefined && (
        <span className="field-hint" id={hintId}>
          {hint}
        </span>
      )}
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy.length > 0 ? describedBy.join(' ') : undefined}
        {...input}
      />
      {error !== undefined && error !== null && (
        // `alert` rather than description alone: most of these appear *after* a
        // submission, and a message tied only to the field is announced when the
        // field is next focused — which may be never.
        <span className="field-error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export type SelectFieldProps = CommonProps &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'aria-describedby'> & {
    children: ReactNode;
  };

export function SelectField({
  label,
  hint,
  labelHidden = false,
  error,
  children,
  ...select
}: SelectFieldProps): ReactNode {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint === undefined ? '' : hintId, error ? errorId : ''].filter(Boolean);

  return (
    <div className="field">
      <label htmlFor={id} className={labelHidden ? 'visually-hidden' : undefined}>
        {label}
      </label>
      {hint !== undefined && (
        <span className="field-hint" id={hintId}>
          {hint}
        </span>
      )}
      <select
        id={id}
        aria-describedby={describedBy.length > 0 ? describedBy.join(' ') : undefined}
        {...select}
      >
        {children}
      </select>
      {error !== undefined && error !== null && (
        // `alert` rather than description alone: most of these appear *after* a
        // submission, and a message tied only to the field is announced when the
        // field is next focused — which may be never.
        <span className="field-error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export type TextAreaFieldProps = CommonProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'aria-describedby' | 'aria-invalid'>;

/** The same wiring as `TextField`, for a list somebody types a line at a time. */
export function TextAreaField({
  label,
  hint,
  error,
  labelHidden = false,
  ...area
}: TextAreaFieldProps): ReactNode {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint === undefined ? '' : hintId, error ? errorId : ''].filter(Boolean);

  return (
    <div className="field">
      <label htmlFor={id} className={labelHidden ? 'visually-hidden' : undefined}>
        {label}
      </label>
      {hint !== undefined && (
        <span className="field-hint" id={hintId}>
          {hint}
        </span>
      )}
      <textarea
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy.length > 0 ? describedBy.join(' ') : undefined}
        {...area}
      />
      {error !== undefined && error !== null && (
        <span className="field-error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
